import * as types from './mutations-types';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import {
  createSnippet,
  deleteSnippet,
  updateSnippet,
} from '@/graphql/mutations';
import { listSnippets } from '@/graphql/queries';
import { getCurUser } from '../../../constants/fromLocalStorageConstants';
import { logError } from '@/utils/loggerUtils';

async function downloadFile(key, identityID) {
  try {
    const result = await Storage.get(key, {
      download: true,
      level: 'protected',
      identityId: identityID,
    });
    return result.Body.text();
  } catch (error) {
    logError(error);
    return '';
  }
}

//https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
const hashCode = function (str, seed = 0) {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString();
};

function getPayloadHashCode(query, searchMode, docId) {
  return hashCode(query + searchMode + docId);
}

export const actions = {
  async UPDATE_SNIPPET({ commit }, payload) {
    try {
      const snippet = await API.graphql(
        graphqlOperation(updateSnippet, {
          input: {
            id: payload.id,
            description: payload.description,
          },
        })
      );
      commit(types.UPDATE_SNIPPET_SUCCESS, snippet.data.updateSnippet);
    } catch (error) {
      logError(error);
    }
  },

  async SAVE_SNIPPET({ commit }, payload) {
    commit(types.SAVE_SNIPPET_STARTED, { hitIdx: payload.hitIdx });
    try {
      const projectID = payload.project;

      //upload the hit file to S3 bucket with hash of the hit content
      const hitHashCode = getPayloadHashCode(
        payload.query,
        payload.mode,
        payload.hit.fields.doc_id[0]
      );

      const result = await Storage.put(
        hitHashCode,
        JSON.stringify(payload.hit),
        {
          contentType: 'text/plain',
          resumable: true,
          level: 'protected',
        }
      );
      const response = await API.graphql(
        graphqlOperation(createSnippet, {
          input: {
            hit: {
              bucket: 'dummy',
              region: 'dummy',
              key: result.key,
            },
            accessGroups: getCurUser().groups,
            pageNum: payload.pageNum,
            hitIdx: payload.hitIdx,
            hitHashCode: hitHashCode,
            owner: getCurUser().email,
            identityID: getCurUser().identityID,
            projectID: projectID,
            description: payload.description,
            query: payload.query,
            mode: payload.mode,
            filters: JSON.stringify(payload.filters),
            actionDateStart: payload.actionDateStart,
            actionDateEnd: payload.actionDateEnd,
          },
        })
      );
      const savePayload = {
        snippet: response.data.createSnippet,
        hitContent: JSON.stringify(payload.hit),
        hitIdx: payload.hitIdx,
      };
      commit(types.SAVE_SNIPPET_SUCCESS, savePayload);
      commit(types.RESET_SNIPPET);
    } catch (e) {
      logError(e);
      commit(types.SAVE_SNIPPET_FAILURE, payload);
    }
  },

  async FETCH_SNIPPETS({ commit }, payload = null) {
    try {
      const project_id = payload.project_id;
      const data = await API.graphql(
        graphqlOperation(listSnippets, {
          filter: {
            projectID: {
              eq: project_id,
            },
          },
          limit: 1000,
        })
      );
      const snippets = data.data.listSnippets.items;
      if (snippets.length < 1) {
        commit(types.FETCH_SNIPPETS_SUCCESS, []);
        return 'NO_SNIPPETS_FOR_PROJECT';
      } else {
        const promises = snippets.map((snippet) => {
          return downloadFile(snippet.hit.key, snippet.identityID);
        });

        await Promise.all(promises).then((resultGroup) => {
          for (let i = 0; i < snippets.length; i++) {
            snippets[i].hit_content = resultGroup[i];
          }
        });
        commit(types.FETCH_SNIPPETS_SUCCESS, snippets);
        return 'FETCH_SNIPPETS_SUCCESS';
      }
    } catch (e) {
      logError(e);
      return 'FETCH_SNIPPETS_FAILURE';
    }
  },

  async DESTROY_SNIPPET({ commit }, payload) {
    commit(types.DESTROY_SNIPPET_STARTED, {
      hitIdx: payload.hitIdx,
    });
    try {
      if (payload.snippetId) {
        const response = await API.graphql(
          graphqlOperation(deleteSnippet, {
            input: {
              id: payload.snippetId,
            },
          })
        );
        commit(types.DESTROY_SNIPPET_SUCCESS, {
          snippet: response.data.deleteSnippet,
          hitIdx: payload.hitIdx,
        });
      } else {
        commit(types.DESTROY_SNIPPET_SUCCESS, {
          snippet: undefined,
          hitIdx: payload.hitIdx,
        });
      }
    } catch (error) {
      logError(error);
      commit(types.DESTROY_SNIPPET_FAILURE, { error, hitIdx: payload.hitIdx });
    }
    commit(types.RESET_SNIPPET);
  },

  SNIPPET_EXISTS({ state }, payload) {
    const docIds = payload.docIds;
    state.snippetExistsArray = [];
    for (let i = 0; i < docIds.length; i++) {
      state.snippetExistsArray.push(
        state.activeSnippetHashes.includes(
          getPayloadHashCode(payload.query, payload.mode, docIds[i])
        )
      );
    }
  },
};
